import { cn } from "@/lib/utils";
import { panelRadiusClass } from "../chat/branding";
import { InputOTP6 } from "@/components/molecules/otp";
import { PhoneInput } from "@/components/molecules/phone";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
} from "@/components/molecules/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { usePublicTranslation } from "@/features/utils/i18n";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { usePublicCustomer } from "../../hooks/use-public-customer";
import {
  usePublicLoginChallenge,
  usePublicLoginVerify,
  usePublicPhoneHandoff,
} from "../../hooks/use-public-login";

const RESEND_SECONDS = 30;

/**
 * One sign-in on screen at a time. Several things ask for it, each with a dialog of its
 * own — the agent's `verify_identity`, the booking calendar, an appointment to confirm,
 * "Me connecter" — and nothing kept two of them from opening together: two code boxes
 * stacked, two SMS. The first one opened is shown; any other opened meanwhile waits behind
 * it. The one code signs them all in (with the agent's blocked action resumed if any of
 * them was for it), and closing the one shown closes the others: the visitor said no once.
 */
type SignInRequest = {
  id: number;
  wantsResume: () => boolean;
  finish: (signedIn: boolean) => void;
};
let signInRequests: SignInRequest[] = [];
let nextRequestId = 0;
const signInListeners = new Set<() => void>();
const setSignInRequests = (next: SignInRequest[]) => {
  signInRequests = next;
  signInListeners.forEach((listener) => listener());
};
const subscribeSignIn = (listener: () => void) => {
  signInListeners.add(listener);
  return () => {
    signInListeners.delete(listener);
  };
};

interface LoginDialogProps {
  publicId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  // When the dialog was opened because a gated tool needs auth, ask the backend
  // to resume the blocked action once verification succeeds.
  resume?: boolean;
  // A number we already hold — the host page handed it over (`HeyUpChat({ customer })`),
  // and nobody proved it is theirs. Without one, the number the visitor declared to book
  // (the calendar's declarative mode) is offered instead.
  initialPhone?: string;
  // Send the code straight away and open on the 6 digits. Only meaningful with
  // `initialPhone`: there is no number to text otherwise.
  autoSend?: boolean;
  // Offer "continue by phone" next to the SMS code: the agent calls the number and the
  // conversation carries on there. It signs nothing in — only the code does.
  phoneHandoff?: boolean;
  // The call is placed: hand the chat over to the "appel en cours" screen, which closes
  // once the visitor has confirmed it is them on the phone.
  onCallStarted?: (handoff: { phone: string }) => void;
}

// "Me connecter": two-step phone OTP (enter phone → enter 6-digit code) — or, when the
// connector allows it, be called on that number instead of waiting for the code.
export function LoginDialog({
  publicId,
  open,
  onOpenChange,
  onSuccess,
  resume,
  initialPhone,
  autoSend,
  phoneHandoff,
  onCallStarted,
}: LoginDialogProps) {
  const { t } = usePublicTranslation();
  const challenge = usePublicLoginChallenge(publicId);
  const verify = usePublicLoginVerify(publicId);
  const handoff = usePublicPhoneHandoff(publicId);
  const { data: customer } = usePublicCustomer(publicId);
  // A phone on the file nobody proved: the visitor typed it (a declarative booking, the
  // contact form). Asking for it again is asking them to type what we already show.
  const declaredPhone =
    customer && !customer.phone_is_verified ? customer.phone || "" : "";

  const [step, setStep] = useState<"phone" | "code" | "calling">("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  // Its place in the one-at-a-time queue above, held while it is open.
  const [requestId] = useState(() => ++nextRequestId);
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  const finishRef = useRef<(signedIn: boolean) => void>(() => {});
  finishRef.current = (signedIn) => {
    onOpenChange(false);
    if (signedIn) onSuccess?.();
  };
  useEffect(() => {
    if (!open) return;
    const request: SignInRequest = {
      id: requestId,
      wantsResume: () => !!resumeRef.current,
      finish: (signedIn) => finishRef.current(signedIn),
    };
    setSignInRequests([...signInRequests, request]);
    return () => setSignInRequests(signInRequests.filter((r) => r !== request));
  }, [open, requestId]);
  const shown = useSyncExternalStore(
    subscribeSignIn,
    () => signInRequests[0]?.id === requestId,
  );
  // The ones waiting behind this one, settled with it.
  const waiting = () => signInRequests.filter((r) => r.id !== requestId);
  const close = (signedIn = false) => {
    const others = waiting();
    finishRef.current(signedIn);
    others.forEach((r) => r.finish(signedIn));
  };

  // Reset whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setStep("phone");
      setPhone(initialPhone || "");
      setCode("");
      setError(null);
      setCountdown(0);
    }
  }, [open, initialPhone]);

  // The declared phone may land after the dialog opened: filled in once per opening, so
  // a visitor who clears the field to type another one is not overridden.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!open) {
      prefilledRef.current = false;
      return;
    }
    if (prefilledRef.current || initialPhone || !declaredPhone) return;
    prefilledRef.current = true;
    setPhone((current) => current || declaredPhone);
  }, [open, initialPhone, declaredPhone]);

  // Resend countdown tick.
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  // `to` is explicit so the auto-send below does not have to wait a render for the
  // reset effect's `setPhone` to land — it texts the number it was handed.
  const sendCode = async (to?: string) => {
    const target = (to ?? phone).trim();
    setError(null);
    if (!target) {
      setError(t("login.invalid_phone"));
      return;
    }
    setPhone(target);
    try {
      await challenge.mutateAsync(target);
      setStep("code");
      setCode("");
      setCountdown(RESEND_SECONDS);
    } catch {
      setError(t("login.send_failed"));
    }
  };

  // Opened on a number we were given rather than one the visitor typed: text it at
  // once, so the code is already on its way while they read why we are asking. Armed
  // once per opening — a re-render must not send a second SMS.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoSentRef.current = false;
      return;
    }
    if (!shown || !autoSend || !initialPhone || autoSentRef.current) return;
    autoSentRef.current = true;
    void sendCode(initialPhone);
  }, [open, shown, autoSend, initialPhone]);

  const callMe = async () => {
    const target = phone.trim();
    setError(null);
    if (!target) {
      setError(t("login.invalid_phone"));
      return;
    }
    try {
      await handoff.mutateAsync(target);
      if (onCallStarted) {
        close();
        onCallStarted({ phone: target });
        return;
      }
      setStep("calling");
    } catch {
      setError(t("login.call_failed"));
    }
  };

  const busy = challenge.isPending || handoff.isPending;

  const submitCode = async (value: string) => {
    setError(null);
    try {
      await verify.mutateAsync({
        phone,
        code: value,
        resume: !!resume || waiting().some((r) => r.wantsResume()),
      });
    } catch {
      setError(t("login.invalid_code"));
      setCode("");
      return;
    }
    close(true);
  };

  // Auto-submit as soon as the 6 digits are entered.
  const handleCodeChange = (value: string) => {
    setCode(value);
    if (error) setError(null);
    if (value.length === 6) submitCode(value);
  };

  return (
    <ResponsiveDialog
      open={open && shown}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <ResponsiveDialogContent
        className={cn("sm:max-w-[425px]", panelRadiusClass)}
      >
        <ResponsiveDialogHeader>
          <h2 className="text-lg font-semibold">
            {resume || autoSend ? t("login.title_action") : t("login.title")}
          </h2>
          {/* When the dialog was opened by the agent mid-conversation, say why: an
              unexplained "enter your phone number" prompt reads as a paywall. */}
          {(resume || autoSend) && (
            <p className="text-sm text-muted-foreground">
              {resume ? t("login.action_reason") : t("login.identity_reason")}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {step === "phone"
              ? phoneHandoff
                ? t("login.phone_description_or_call")
                : t("login.phone_description")
              : step === "code"
                ? t("login.code_description", { phone })
                : t("login.calling_description", { phone })}
          </p>
        </ResponsiveDialogHeader>

        <div className="px-4 space-y-4">
          {step === "phone" ? (
            <div className="space-y-2">
              <Label htmlFor="login-phone">{t("login.phone")}</Label>
              <PhoneInput
                id="login-phone"
                placeholder="+33 6 12 34 56 78"
                value={phone}
                onChange={(v) => setPhone(v || "")}
                aria-invalid={!!error}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>
          ) : step === "calling" ? null : (
            <div className="space-y-3">
              <div className="flex justify-center">
                <InputOTP6 value={code} onChange={handleCodeChange} autoFocus />
              </div>
              {error && (
                <p className="text-xs text-center text-destructive">{error}</p>
              )}
              <div className="flex flex-col items-center">
                <Button
                  variant="link"
                  size="sm"
                  disabled={countdown > 0 || challenge.isPending}
                  onClick={() => sendCode()}
                >
                  {countdown > 0
                    ? t("login.resend_in", { seconds: countdown })
                    : t("login.resend")}
                </Button>
                {/* Still waiting for the SMS: switch to the phone without starting over. */}
                {phoneHandoff && (
                  <Button
                    variant="link"
                    size="sm"
                    disabled={busy}
                    onClick={callMe}
                  >
                    {t("login.call_instead")}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <ResponsiveDialogFooter className="flex flex-col items-center gap-2 sm:flex-col sm:justify-center">
          {step === "phone" ? (
            <>
              <Button
                size="lg"
                className={phoneHandoff ? "w-full sm:w-64" : "w-48"}
                onClick={() => sendCode()}
                disabled={busy}
              >
                {challenge.isPending
                  ? t("login.sending")
                  : phoneHandoff
                    ? t("login.send_code_sms")
                    : t("login.send_code")}
              </Button>
              {phoneHandoff && (
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-64"
                  onClick={callMe}
                  disabled={busy}
                >
                  {handoff.isPending
                    ? t("login.calling")
                    : t("login.continue_by_phone")}
                </Button>
              )}
            </>
          ) : step === "calling" ? (
            <Button size="lg" className="w-48" onClick={() => close()}>
              {t("login.close")}
            </Button>
          ) : (
            <Button
              size="lg"
              className="w-48"
              onClick={() => submitCode(code)}
              disabled={verify.isPending || code.length !== 6}
            >
              {verify.isPending ? t("login.verifying") : t("login.verify")}
            </Button>
          )}
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
